<template>
  <!-- Cherry's REAL plans for this option, verbatim — or, when there are none we can stand
       behind, a sentence with NO figures in it. Never the practice's typed tiers: this
       component is the only thing a Cherry-backed option renders in their place.
       One component for four hosts (Present Mode card, patient-view card, Plan Summary, and the
       Builder) so the rows, the disclosure and the "no figures" rule cannot drift between them.

       `selectable` is the BUILDER's mode and nobody else's: every priced plan gets a checkbox so
       the TC can hide plans for THIS patient, and the hidden ones stay on screen (unticked) so they
       can be brought back. Present Mode, the patient link, the plan summary and the PDF never set
       it - they render only the plans that survive the hide lists, read-only. -->
  <div class="ctl" :class="['ctl-' + variant, { 'ctl-selectable': selectable }]" data-test="cherry-tier-list">
    <template v-if="selectable ? editableUsable : usable">
      <div
        v-for="row in displayRows"
        :key="row.key"
        class="ctl-row"
        :class="{ 'ctl-row-off': selectable && !row.shown, 'ctl-row-locked': selectable && row.practiceHidden }"
        data-test="cherry-tier-row"
      >
        <input
          v-if="selectable"
          type="checkbox"
          class="ctl-pick"
          :checked="row.shown"
          :disabled="row.practiceHidden"
          :aria-label="(row.shown ? 'Hide ' : 'Show ') + row.label + ' for this patient'"
          :title="row.practiceHidden ? 'Hidden in Settings for every patient - switch it back on there' : ''"
          data-test="cherry-plan-toggle"
          @change="onToggle(row, $event.target.checked)"
        />
        <span class="ctl-label">
          {{ row.label }}
          <span v-if="selectable && row.practiceHidden" class="ctl-tag" data-test="cherry-hidden-in-settings">Hidden in Settings</span>
        </span>
        <span class="ctl-amt">{{ row.amount }}</span>
        <span class="ctl-note">{{ row.note }}</span>
      </div>
      <p v-if="selectable && allHidden" class="ctl-warn" role="alert" data-test="cherry-all-hidden-warning">
        The patient will see '{{ noTermsText }}'
      </p>
      <p class="ctl-disclosure" data-test="cherry-disclosure">{{ disclosure }}</p>
    </template>
    <p v-else class="ctl-none" data-test="cherry-no-terms">{{ updating ? updatingText : noTermsText }}</p>
  </div>
</template>

<script>
import {
  hasUsableCherryTerms,
  isCherryBacked,
  cherryTermsMatch,
  cherryTierRows,
  cherryEditableRows,
  cherryDisclosure,
  CHERRY_NO_TERMS_TEXT,
} from '../utils/cherryTerms.js';

export default {
  name: 'CherryTierList',
  props: {
    // The finance option being shown (carries provider + the hide lists cherryHiddenPlans /
    // cherryHiddenPlansPatient + the legacy cherryTermMonths filter).
    opt: { type: Object, default: null },
    // Cherry's terms: { purchaseAmount, plans, fetchedAt } or null.
    terms: { type: Object, default: null },
    // The amount these terms must have been priced for (cherryAmountFor(breakdown)).
    amount: { type: Number, default: null },
    // Pre-formatted "as of" date in the site timezone; omitted => the disclosure drops the date.
    asOfText: { type: String, default: '' },
    // Which host surface this draws in — only changes the look, never the content.
    variant: {
      type: String,
      default: 'present',
      validator: (v) => ['present', 'patient', 'summary', 'builder'].indexOf(v) !== -1,
    },
    // A fetch is in flight or the terms are stale: say so instead of "we'll confirm".
    updating: { type: Boolean, default: false },
    // BUILDER ONLY: a checkbox per priced plan, hidden plans kept on screen. Emits `toggle-plan`
    // { key, shown } and nothing else - the host owns persisting it. Default off, so every other
    // surface stays read-only and filtered.
    selectable: { type: Boolean, default: false },
  },
  computed: {
    // Figures are shown ONLY when the terms were priced for exactly this amount and the
    // option's hide lists / filter leave at least one plan. Everything else is the no-figures text.
    usable() {
      return hasUsableCherryTerms(this.opt, this.terms, this.amount);
    },
    // Selectable mode keeps the hidden plans visible, so "usable" there is only "priced for this
    // amount and there is something to toggle" - not "something survives the hide lists".
    editableRows() {
      return this.selectable ? cherryEditableRows(this.terms, this.opt) : [];
    },
    editableUsable() {
      return isCherryBacked(this.opt)
        && cherryTermsMatch(this.terms, this.amount)
        && this.editableRows.length > 0;
    },
    displayRows() {
      if (this.selectable) return this.editableUsable ? this.editableRows : [];
      return this.usable ? cherryTierRows(this.terms, this.opt) : [];
    },
    // Every plan Cherry priced is hidden (practice and/or this patient): the patient gets the
    // no-figures sentence, and the TC is told so before they walk into the room.
    allHidden() {
      return this.editableRows.length > 0 && this.editableRows.every((r) => !r.shown);
    },
    disclosure() {
      return cherryDisclosure(this.asOfText);
    },
    noTermsText() {
      return CHERRY_NO_TERMS_TEXT;
    },
    updatingText() {
      return 'Updating Cherry terms…';
    },
  },
  methods: {
    onToggle(row, checked) {
      // A plan the practice hid cannot be brought back from here (the checkbox is disabled; this
      // is the belt to its braces).
      if (row.practiceHidden) return;
      this.$emit('toggle-plan', { key: row.key, shown: !!checked });
    },
  },
};
</script>

<style scoped>
.ctl {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 6px 0;
  font-variant-numeric: tabular-nums;
}

/* Each plan: label on the left, the payment on the right, the fine print under both. Grid
   (not flex-wrap) so the amount stays pinned right and the note spans the row at any width. */
.ctl-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 10px;
  align-items: baseline;
  padding: 4px 0;
  font-size: 12.5px;
}
.ctl-label { min-width: 0; overflow-wrap: anywhere; }
.ctl-amt {
  font-weight: 700;
  white-space: nowrap;
  text-align: right;
}
.ctl-note {
  grid-column: 1 / -1;
  font-size: 11px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.ctl-disclosure,
.ctl-none {
  margin: 6px 0 0;
  font-size: 10.5px;
  line-height: 1.5;
}
.ctl-none { font-size: 12.5px; margin: 0; }

/* ── Present Mode card (mirrors .pm-finance-tier*) ── */
.ctl-present .ctl-row { border-bottom: 1px solid var(--pm-border, #e4e7ec); }
.ctl-present .ctl-row:last-of-type { border-bottom: none; }
.ctl-present .ctl-label { color: var(--pm-text-mute, #475569); }
.ctl-present .ctl-amt { color: var(--pm-accent, #0d5f54); }
.ctl-present .ctl-note,
.ctl-present .ctl-disclosure { color: var(--pm-text-dim, #94a3b8); }
.ctl-present .ctl-none { color: var(--pm-text-mute, #475569); }

/* ── Patient view card (mirrors .pv-finance-tier*) ── */
.ctl-patient { margin-top: 10px; border-top: 1px solid #eef1f4; }
.ctl-patient .ctl-row { padding: 5px 0; border-bottom: 1px solid #f1f4f7; }
.ctl-patient .ctl-row:last-of-type { border-bottom: none; }
.ctl-patient .ctl-label { color: #475569; }
.ctl-patient .ctl-amt { font-weight: 600; color: #101820; }
.ctl-patient .ctl-note,
.ctl-patient .ctl-disclosure { color: #94a3b8; }
.ctl-patient .ctl-none { color: #475569; padding-top: 6px; }

/* ── Plan Summary chosen-option rows (mirrors .tps-chosen-row / -sched-row) ── */
.ctl-summary { margin: 0; }
.ctl-summary .ctl-row { padding: 3px 0; font-size: 12px; }
.ctl-summary .ctl-label { color: #64748b; font-weight: 500; }
.ctl-summary .ctl-amt { color: #0f172a; }
.ctl-summary .ctl-note,
.ctl-summary .ctl-disclosure { color: #94a3b8; }
.ctl-summary .ctl-none { color: #64748b; font-size: 12px; }

/* ── Builder (right pane, per-patient plan switches) ── */
.ctl-builder { margin: 8px 0 2px; }
.ctl-builder .ctl-row { padding: 5px 0; border-bottom: 1px solid var(--txb-border, #e4e7ec); }
.ctl-builder .ctl-row:last-of-type { border-bottom: none; }
.ctl-builder .ctl-label { color: var(--txb-text, #334155); }
.ctl-builder .ctl-amt { color: var(--txb-accent, #0f6e63); }
.ctl-builder .ctl-note,
.ctl-builder .ctl-disclosure { color: var(--txb-text-dim, #94a3b8); }
.ctl-builder .ctl-none { color: var(--txb-text-mute, #475569); }

/* Selectable (Builder): a checkbox column in front of the label. The note keeps spanning the
   whole row (grid-column: 1 / -1 above), so it sits under checkbox + label + amount. */
.ctl-selectable .ctl-row {
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
}
.ctl-pick {
  margin: 0;
  width: 15px;
  height: 15px;
  accent-color: var(--txb-accent, #0f6e63);
  cursor: pointer;
}
.ctl-pick:disabled { cursor: not-allowed; }
/* Hidden for this patient: still readable (so it can be brought back), clearly not on offer. */
.ctl-row-off .ctl-label,
.ctl-row-off .ctl-amt,
.ctl-row-off .ctl-note { opacity: 0.5; }
.ctl-row-off .ctl-label { text-decoration: line-through; }
/* Hidden in Settings: greyed and locked - the Builder cannot bring it back. */
.ctl-row-locked { background: rgba(148, 163, 184, 0.1); }
.ctl-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #e2e8f0;
  color: #475569;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.1px;
  text-decoration: none;
  white-space: nowrap;
}
.ctl-warn {
  margin: 6px 0 0;
  padding: 6px 9px;
  font-size: 11.5px;
  line-height: 1.45;
  color: #78350f;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
}

/* Print — a plan must not split across pages with its disclosure on the next one. */
@media print {
  .ctl { break-inside: avoid; }
  .ctl-row { break-inside: avoid; }
}
</style>
